import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Subject, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AuthenticationService } from "../auth/authentication.service";

@Injectable({
  providedIn: "root",
})
export class SiteService {
  private ENDPOINT_SITE = "/v4/sites";
  private ENDPOINT_SITE_POST = "/sites";
  private ENDPOINT_SITE_DELETE = "/sites";
  private ENDPOINT_SITE_V5 = "/v4-1/sites";
  private ENDPOINT_SITE_TYPES = "/site-types";
  private ENDPOINT_SITE_AREAS = "/site-areas";
  private ENDPOINT_SITE_CATEGORIES = "/site-categories";
  private ENDPOINT_SITE_SUB_CATEGORIES = "/site-sub-categories";
  private ENDPOINT_SITE_PICKLIST = "/sites/pick-list";
  private UPDLOAD_PARENT_TYPE = "SITE_ATTACHMENT";
  private ENDPOINT_SITE_CONTACTS_BY_SITE = "/contacts/by-site";
  private ENDPOINT_SITE_CONTACTS = "/contacts";
  private ENDPOINT_GET_ATTACHMENTS_BY_SITE_ID = "/v4/attachments/site";
  //endpoints related to Site's Block/Level/Area
  private ENDPOINT_SITE_BLOCKS = "/site-blocks";
  private ENDPOINT_SITE_LEVELS = "/site-levels";
  private ENDPOINT_SITE_AREA = "/site-block-areas";
  private ENDPOINT_SITE_WORK_SCOPE = "/site-work-scopes";

  private ENDPOINT_SITE_CONTRACT = "/v4/contracts";
  private ENDPOINT_SKILL_LIBRARY = "/skill-library";
  private ENDPOINT_JOB_ORDERS = "/v5/job-orders";
  private ENDPOINT_ASSIGNEE_USER = "/v5/users/active/availability";
  private ENDPOINT_APPEND_ASSETS_TO_BLOCK = "/assets/location";
  private ENDPOINT_DELETE_ASSET_FROM_SITE = "/assets/unlink/site";


  private selectSubject = new Subject();

  public select(val: any) {
    this.selectSubject.next(val);
  }

  public getSelectItem() {
    return this.selectSubject.asObservable();
  }

  constructor(
    private http: HttpClient,
    private auth: AuthenticationService,
  ) {}

  public getAllSites(
    serverParams: any,
    searchValue: string | null,
    customerId: any | null,
    projectId: any | null,
    typeId: any | null,
    serviceZoneId: any | null,
    categoryId: any | null,
    geofenceId: any | null,
    origin: any | null,
    siteIdsToSkip: any | null,
    assetsId: any = null,
  ) {
    let params = new HttpParams();

    if (searchValue !== "" && searchValue != null) {
      params = params.set("search", searchValue);
    }

    if (customerId && customerId !== "" && customerId != null) {
      params = params.set("customerId", String(customerId));
    }

    if (projectId && projectId !== "" && projectId != null) {
      params = params.set("projectId", String(projectId));
    }

    if (typeId && typeId !== "" && typeId != null) {
      params = params.set("typeId", String(typeId));
    }

    if (serviceZoneId && serviceZoneId !== "" && serviceZoneId != null) {
      params = params.set("serviceZoneId", String(serviceZoneId));
    }

    if (categoryId && categoryId !== "" && categoryId != null) {
      params = params.set("categoryId", String(categoryId));
    }

    if (geofenceId && geofenceId !== "" && geofenceId != null) {
      params = params.set("geofenceId", String(geofenceId));
    }

    if (origin && origin !== "" && origin != null) {
      params = params.set("origin", String(origin));
    }

    if (siteIdsToSkip !== "" && siteIdsToSkip != null) {
      params = params.set(
        "siteIdsToSkip",
        Array.isArray(siteIdsToSkip)
          ? siteIdsToSkip.join(",")
          : String(siteIdsToSkip),
      );
    }

    if (assetsId) {
      params = params.set("assetsId", String(assetsId));
    }

    if (serverParams) {
      params = params
        .set("startFrom", serverParams.startRow)
        .set("perPage", "25")
        .set("columnName", (serverParams.sortModel[0] as any).colId)
        .set("sortOrder", (serverParams.sortModel[0] as any).sort);
    }

    return this.http
      .get(`${environment.base_url}${this.ENDPOINT_SITE_V5}`, {
        observe: "response",
        params: params,
      })
      .pipe(
        map((resp: any) => {
          if (resp.status === 200) {
            return resp.body;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  /******************************************** TYPES ******************************************/
  public getAllSiteTypes(isActive: boolean | null, isOrder: boolean | null) {
    let params = new HttpParams();

    if (isActive !== null && (isActive === true || isActive === false)) {
      params = params.set("isActive", String(isActive));
    }

    if (isOrder !== null && (isOrder === true || isOrder === false)) {
      params = params.set("isOrder", String(isOrder));
    }

    return this.http
      .get(`${environment.base_url}${this.ENDPOINT_SITE_TYPES}`, {
        observe: "response",
        params: params,
      })
      .pipe(
        map((resp: any) => {
          if (resp.status === 200) {
            return resp.body;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public createSiteType(formData: any) {
    return this.http
      .post(`${environment.base_url}${this.ENDPOINT_SITE_TYPES}`, formData)
      .pipe(
        map((resp: any) => {
          if (resp) {
            return resp;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public updateSiteType(formData: any) {
    return this.http
      .put(`${environment.base_url}${this.ENDPOINT_SITE_TYPES}`, formData)
      .pipe(
        map((resp: any) => {
          if (resp) {
            return resp;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public deleteSiteType(id: any) {
    return this.http
      .delete(
        `${environment.base_url}${this.ENDPOINT_SITE_TYPES}/${String(id)}`,
      )
      .pipe(
        map((resp: any) => {
          console.log(resp);
          if (resp) {
            return resp;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  /******************************************** AREAS ******************************************/
  public getAllSiteAreas(isActive: boolean | null, isOrder: boolean | null) {
    let params = new HttpParams();

    if (isActive !== null && (isActive === true || isActive === false)) {
      params = params.set("isActive", String(isActive));
    }

    if (isOrder !== null && (isOrder === true || isOrder === false)) {
      params = params.set("isOrder", String(isOrder));
    }

    return this.http
      .get(`${environment.base_url}${this.ENDPOINT_SITE_AREAS}`, {
        observe: "response",
        params: params,
      })
      .pipe(
        map((resp: any) => {
          if (resp.status === 200) {
            return resp.body;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public createSiteArea(formData: any) {
    return this.http
      .post(`${environment.base_url}${this.ENDPOINT_SITE_AREAS}`, formData)
      .pipe(
        map((resp: any) => {
          if (resp) {
            return resp;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public updateSiteArea(formData: any) {
    return this.http
      .put(`${environment.base_url}${this.ENDPOINT_SITE_AREAS}`, formData)
      .pipe(
        map((resp: any) => {
          if (resp) {
            return resp;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public deleteSiteArea(id: any) {
    return this.http
      .delete(
        `${environment.base_url}${this.ENDPOINT_SITE_AREAS}/${String(id)}`,
      )
      .pipe(
        map((resp: any) => {
          console.log(resp);
          if (resp) {
            return resp;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  /******************************************** CATEGORIES ******************************************/
  public getAllSiteCategories(
    isActive: boolean | null,
    isOrder: boolean | null,
  ) {
    let params = new HttpParams();

    if (isActive !== null && (isActive === true || isActive === false)) {
      params = params.set("isActive", String(isActive));
    }

    if (isOrder !== null && (isOrder === true || isOrder === false)) {
      params = params.set("isOrder", String(isOrder));
    }

    return this.http
      .get(`${environment.base_url}${this.ENDPOINT_SITE_CATEGORIES}`, {
        observe: "response",
        params: params,
      })
      .pipe(
        map((resp: any) => {
          if (resp.status === 200) {
            return resp.body;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public createSiteCategory(formData: any) {
    return this.http
      .post(`${environment.base_url}${this.ENDPOINT_SITE_CATEGORIES}`, formData)
      .pipe(
        map((resp: any) => {
          if (resp) {
            return resp;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public updateSiteCategory(formData: any) {
    return this.http
      .put(`${environment.base_url}${this.ENDPOINT_SITE_CATEGORIES}`, formData)
      .pipe(
        map((resp: any) => {
          if (resp) {
            return resp;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public deleteSiteCategory(id: any) {
    return this.http
      .delete(
        `${environment.base_url}${this.ENDPOINT_SITE_CATEGORIES}/${String(id)}`,
      )
      .pipe(
        map((resp: any) => {
          console.log(resp);
          if (resp) {
            return resp;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  /******************************************** SUB CATEGORIES ******************************************/
  public getAllSiteSubCategories(
    isActive: boolean | null,
    isOrder: boolean | null,
    siteCategoryId: any | null,
  ) {
    let params = new HttpParams();

    if (isActive !== null && (isActive === true || isActive === false)) {
      params = params.set("isActive", String(isActive));
    }

    if (isOrder !== null && (isOrder === true || isOrder === false)) {
      params = params.set("isOrder", String(isOrder));
    }

    if (siteCategoryId && siteCategoryId !== null) {
      params = params.set("siteCategoryId", String(siteCategoryId));
    }

    console.log(params);

    return this.http
      .get(`${environment.base_url}${this.ENDPOINT_SITE_SUB_CATEGORIES}`, {
        observe: "response",
        params: params,
      })
      .pipe(
        map((resp: any) => {
          if (resp.status === 200) {
            return resp.body;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public createSiteSubCategory(formData: any) {
    return this.http
      .post(
        `${environment.base_url}${this.ENDPOINT_SITE_SUB_CATEGORIES}`,
        formData,
      )
      .pipe(
        map((resp: any) => {
          if (resp) {
            return resp;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public updateSiteSubCategory(formData: any) {
    return this.http
      .put(
        `${environment.base_url}${this.ENDPOINT_SITE_SUB_CATEGORIES}`,
        formData,
      )
      .pipe(
        map((resp: any) => {
          if (resp) {
            return resp;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public deleteSiteSubCategory(id: any) {
    return this.http
      .delete(
        `${environment.base_url}${this.ENDPOINT_SITE_SUB_CATEGORIES}/${String(
          id,
        )}`,
      )
      .pipe(
        map((resp: any) => {
          console.log(resp);
          if (resp) {
            return resp;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public getListColumn(apiModule: any) {
    return this.http
      .get(
        environment.base_url +
          "/table-column-setups/by-module?apiModule=" +
          apiModule,
      )
      .pipe(
        map((resp: any) => {
          return resp;
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public createSite(formData: any) {
    return this.http
      .post(`${environment.base_url}${this.ENDPOINT_SITE_POST}`, formData)
      .pipe(
        map((resp: any) => {
          if (resp) {
            return resp;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public deleteSite(id: any) {
    return this.http
      .delete(
        `${environment.base_url}${this.ENDPOINT_SITE_DELETE}/${String(id)}`,
      )
      .pipe(
        map((resp: any) => {
          console.log(resp);
          if (resp) {
            return resp;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public getSiteById(id: any) {
    return this.http
      .get(`${environment.base_url}${this.ENDPOINT_SITE}/${String(id)}`)
      .pipe(
        map((resp: any) => {
          return resp;
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public updateSite(formData: any) {
    return this.http
      .put(`${environment.base_url}${this.ENDPOINT_SITE_POST}`, formData)
      .pipe(
        map((resp: any) => {
          if (resp) {
            return resp;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public getSitePicklist(search: string | null, customerId?: string | null, projectId?: any, assetsId?: any) {
    let params = new HttpParams();

    if (search !== null && search !== "") {
      params = params.set("search", String(search));
    }

    if (customerId && customerId !== "") {
      params = params.set("customerId", String(customerId));
    }

    if (projectId) {
      params = params.set("projectId", String(projectId));
    }

    if (assetsId) {
      params = params.set("assetsId", String(assetsId));
    }
    return this.http
      .get(`${environment.base_url}${this.ENDPOINT_SITE_PICKLIST}`, {
        observe: "response",
        params: params,
      })
      .pipe(
        map((resp: any) => {
          if (resp.status === 200) {
            return resp.body;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public getSkillLibrary(
    page: number,
    perPage: number,
    sortBy: string = "name",
    sortOrder: string = "asc",
  ) {
    let params = new HttpParams()
      .set("page", page)
      .set("perPage", perPage)
      .set("sortBy", sortBy)
      .set("sortOrder", sortOrder);

    return this.http
      .get(`${environment.base_url}${this.ENDPOINT_SKILL_LIBRARY}`, {
        observe: "response",
        params: params,
      })
      .pipe(
        map((resp: any) => {
          if (resp.status === 200) {
            return resp.body;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public uploadAttachment(id: any, formData: any) {
    const headers = new HttpHeaders({
      Authorization: "Bearer " + this.auth.getToken(),
    });

    return this.http
      .post(
        environment.base_url +
          `/attachments/upload?parentType=${this.UPDLOAD_PARENT_TYPE}&parentId=` +
          id,
        formData,
        { headers: headers, observe: "response" },
      )
      .pipe(
        map((resp: any) => {
          return resp;
        }),
        catchError((error: any) => {
          //console.log(error);
          return throwError(error);
        }),
      );
  }

  public getSiteContacts(id: any | null, searchValue?: string) {
    let params = new HttpParams();

    if (id !== null && id !== "") {
      params = params.set("siteId", String(id));
    }
    if (searchValue) {
      params = params.set("search", searchValue);
    }
    return this.http
      .get(`${environment.base_url}${this.ENDPOINT_SITE_CONTACTS_BY_SITE}`, {
        observe: "response",
        params: params,
      })
      .pipe(
        map((resp: any) => {
          if (resp.status === 200) {
            return resp.body;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  /************************************* Get Site Attachments  Info By ID *************************************************************************/
  public getAttachmentsBySiteId(id: any) {
    let params = new HttpParams();
    params = params.set("siteId", id);
    return this.http
      .get(environment.base_url + this.ENDPOINT_GET_ATTACHMENTS_BY_SITE_ID, {
        params: params,
      })
      .pipe(
        map((resp: any) => {
          return resp;
        }),
        catchError((error) => {
          ////console.log(error);
          return throwError(error);
        }),
      );
  }

  public createSiteContact(formData: any) {
    return this.http
      .post(`${environment.base_url}${this.ENDPOINT_SITE_CONTACTS}`, formData)
      .pipe(
        map((resp: any) => {
          if (resp) {
            return resp;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public updateSiteContact(formData: any) {
    return this.http
      .put(`${environment.base_url}${this.ENDPOINT_SITE_CONTACTS}`, formData)
      .pipe(
        map((resp: any) => {
          if (resp) {
            return resp;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  public getSiteTasks(siteId: any) {
    return this.http.get(`${environment.base_url}/sites/${siteId}/tasks`).pipe(
      map((resp: any) => {
        if (resp) {
          return resp;
        } else {
          throwError(resp);
        }
      }),
      catchError((error) => {
        return throwError(error);
      }),
    );
  }

  /*APIs related to Site's Block, Level, Area*/
  getBlocksBySite(siteId: any) {
    //site-blocks/by-site/1 ENDPOINT_SITE_BLOCKS
    // return this.http.get(`${environment.base_url}/sites/${siteId}/tasks`).pipe(
    return this.http
      .get(
        `${environment.base_url}${this.ENDPOINT_SITE_BLOCKS}/by-site/${siteId}`,
        { observe: "response" },
      )
      .pipe(
        map((resp: any) => resp),
        catchError((error) => throwError(() => error)),
      );
  }

  createBlock(formData: any, layer?: string) {
    let apiUrl: string;
    if (layer == "block") {
      apiUrl = `${environment.base_url}${this.ENDPOINT_SITE_BLOCKS}`;
    } else if (layer == "level") {
      apiUrl = `${environment.base_url}${this.ENDPOINT_SITE_LEVELS}`;
    } else if (layer == "area") {
      apiUrl = `${environment.base_url}${this.ENDPOINT_SITE_AREA}`;
    } else {
      apiUrl = `${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}`;
    }

    return this.http.post(apiUrl, formData, { observe: "response" }).pipe(
      map((resp: any) => resp),
      catchError((error) => throwError(() => error)),
    );
  }

  updateBlock(formData: any, layer?: string) {
    let apiUrl: string;
    if (layer == "block") {
      apiUrl = `${environment.base_url}${this.ENDPOINT_SITE_BLOCKS}`;
    } else if (layer == "level") {
      apiUrl = `${environment.base_url}${this.ENDPOINT_SITE_LEVELS}`;
    } else if (layer == "area") {
      apiUrl = `${environment.base_url}${this.ENDPOINT_SITE_AREA}`;
    } else {
      apiUrl = `${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}`;
    }

    return this.http.put(apiUrl, formData, { observe: "response" }).pipe(
      map((resp: any) => resp),
      catchError((error) => throwError(() => error)),
    );
  }

  getBlockById(layer?: string, id?: any) {
    let apiUrl: string;
    if (layer == "block") {
      apiUrl = `${environment.base_url}${this.ENDPOINT_SITE_BLOCKS}/${id}`;
    } else if (layer == "level") {
      apiUrl = `${environment.base_url}${this.ENDPOINT_SITE_LEVELS}/${id}`;
    } else if (layer == "area") {
      apiUrl = `${environment.base_url}${this.ENDPOINT_SITE_AREA}/${id}`;
    } else {
      apiUrl = `${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}/${id}`;
    }

    return this.http.get(apiUrl, { observe: "response" }).pipe(
      map((resp: any) => resp),
      catchError((error) => throwError(() => error)),
    );
  }

  getSiteLevelsByBlock(blockId: any) {
    return this.http
      .get(
        `${environment.base_url}${this.ENDPOINT_SITE_LEVELS}/by-block/${blockId}`,
        { observe: "response" },
      )
      .pipe(
        map((resp: any) => resp),
        catchError((error) => throwError(() => error)),
      );
  }

  getSiteAreasByLevel(levelId: any, contractId?: any) {
    //site-block-areas/by-level/1
    let params = new HttpParams()
    if (contractId) {
      params = params.set("contractId", contractId);
    }
    return this.http
      .get(
        `${environment.base_url}${this.ENDPOINT_SITE_AREA}/by-level/${levelId}`,
        { observe: "response", params: params },
      )
      .pipe(
        map((resp: any) => resp),
        catchError((error) => throwError(() => error)),
      );
  }

  getSiteAreasById(id: any) {
    return this.http
      .get(`${environment.base_url}${this.ENDPOINT_SITE_AREA}/${id}`, {
        observe: "response",
      })
      .pipe(
        map((resp: any) => resp),
        catchError((error) => throwError(() => error)),
      );
  }

  deleteBlock(id: any, layer?: string) {
    let apiUrl: string;
    if (layer == "Block") {
      apiUrl = `${environment.base_url}${this.ENDPOINT_SITE_BLOCKS}/${id}`;
    } else if (layer == "Level") {
      apiUrl = `${environment.base_url}${this.ENDPOINT_SITE_LEVELS}/${id}`;
    } else if (layer == "Area") {
      apiUrl = `${environment.base_url}${this.ENDPOINT_SITE_AREA}/${id}`;
    } else {
      apiUrl = `${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}/${id}`;
    }

    return this.http.delete(apiUrl, { observe: "response" }).pipe(
      map((resp: any) => resp),
      catchError((error) => throwError(() => error)),
    );
  }

  createContract(formData: any) {
    let apiUrl: string = `${environment.base_url}${this.ENDPOINT_SITE_CONTRACT}`;
    return this.http.post(apiUrl, formData, { observe: "response" }).pipe(
      map((resp: any) => resp),
      catchError((error) => throwError(() => error)),
    );
  }

   getContractById(id: any) {
    return this.http
      .get(environment.base_url + this.ENDPOINT_SITE_CONTRACT + "/"+id, {
        observe: "response",
      })
      .pipe(
        map((resp: any) => resp),
        catchError((error) => throwError(() => error)),
      );
  }

  public getSiteWorkScopeList(
    page: any,
    perPage: number,
    sortBy: string = "name",
    sortOrder: string = "asc",
    siteBlockAreaId?: any,
    contractId?: any,
    siteId?: any,
    siteBlockId?: any,
    siteLevelId?: any,
    joTemplateId?: any,
    search?: any,
    startFrom?: any
  ) {
    let params = new HttpParams()
      //.set("page", page)
      .set("perPage", perPage)
      .set("columnName", sortBy)
      .set("sortOrder", sortOrder);

    if (page) {
      params = params.set("page", page);
    }
    if (startFrom){
      params = params.set("startFrom", startFrom);
    }
      if (siteBlockAreaId && siteBlockAreaId.length) {
        params = params.set("siteBlockAreaIds", siteBlockAreaId);
      }
    if (contractId && contractId.length) {
      params = params.set("contractIds", contractId);
    }
    if (siteId && siteId.length) {
      params = params.set("siteIds", siteId);
    }
    if (siteBlockId && siteBlockId.length) {
      params = params.set("siteBlockIds", siteBlockId);
    }
    if (siteLevelId && siteLevelId.length) {
      params = params.set("siteLevelIds", siteLevelId);
    }
    if (joTemplateId && joTemplateId.length) {
      params = params.set("jobOrderTemplateIds", joTemplateId);
    }
    if (search) {
      params = params.set("search", search);
    }


    return this.http
      .get(`${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}/paged`, {
        observe: "response",
        params: params,
      })
      .pipe(
        map((resp: any) => {
          if (resp.status === 200) {
            return resp.body;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  getMetaToGenerateJob(custId: any, contractId: any, siteIds?: any, blockIds?: any, levelIds?: any ) {
    //site-work-scopes/filter-meta?customerId=56&contractId=29
    return this.http
      .get(
        `${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}/filter-meta?customerId=${custId}&contractId=${contractId}${siteIds && siteIds.length ? `&siteIds=${siteIds}` : ''}${blockIds && blockIds.length ? `&siteBlockIds=${blockIds}` : ''}${levelIds && levelIds.length ? `&siteLevelIds=${levelIds}` : ''}`,
        {
          observe: "response",
        },
      )
      .pipe(
        map((resp: any) => resp),
        catchError((error) => throwError(() => error)),
      );
  }

  generateJobOrder(body: any) {
    //site-work-scopes/generate-jobs
    let apiUrl: string = `${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}/generate-jobs`;
    return this.http.post(apiUrl, body, { observe: "response" }).pipe(
      map((resp: any) => resp),
      catchError((error) => throwError(() => error)),
    );
  }

  getEstimateJobCount(body: any) {
    //site-work-scopes/estimate-jobs;
    let apiUrl: string = `${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}/estimate-jobs`;
    return this.http.post(apiUrl, body, { observe: "response" }).pipe(
      map((resp: any) => resp),
      catchError((error) => throwError(() => error)),
    );
  }

  getOngoingJobOrderGeneration() {
    //tells whether a queued JO generation is currently running
    return this.http
      .get(
        `${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}/generations/ongoing`,
        {
          observe: "response",
        },
      )
      .pipe(
        map((resp: any) => resp),
        catchError((error) => throwError(() => error)),
      );
  }

  getGeneratedJobOrdersInfo(processId: any){
    //this is for queue 
    return this.http
      .get(
        `${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}/generations/${processId}`,
        {
          observe: "response",
        },
      )
      .pipe(
        map((resp: any) => resp),
        catchError((error) => throwError(() => error)),
      );

  }

  getJobOrdersList(
    page: number,
    perPage: number,
    sortBy: string = "name",
    sortOrder: string = "asc",
    search: string | null,
    body: any,
  ) {
    let params = new HttpParams()
      .set("page", page)
      .set("perPage", perPage)
      .set("columnName", sortBy) //sortBy
      .set("sortOrder", sortOrder);

    if (search) {
      params = params.set("search", search);
    }

    return this.http
      .post(
        `${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}/generated-jobs/paged`,
        body,
        {
          observe: "response",
          params: params,
        },
      )
      .pipe(
        map((resp: any) => {
          if (resp.status === 200) {
            return resp.body;
          } else {
            throwError(resp);
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  getWorkScopeUsers(
    page: number,
    perPage: number,
    sortBy: string = "name",
    sortOrder: string = "asc",
    search: string | null,
    filterParams: any,
  ) {
    //site-work-scopes/filter-meta/users?page=1&perPage=10&startFrom=&columnName=id&sortOrder=asc&search=&contractId=31&siteIds=1&customerId=46&siteBlockIds=9&siteAreaIds=23&frequencies
    let params = new HttpParams()
      .set("page", page)
      .set("perPage", perPage)
      .set("columnName", sortBy) //sortBy
      .set("sortOrder", sortOrder);

    if (search) {
      params = params.set("search", search);
    }

    if (filterParams) {
      if (filterParams.contractId) {
        params = params.set("contractId", filterParams.contractId);
      }
      if (filterParams.customerId) {
        params = params.set("customerId", filterParams.customerId);
      }
      if (filterParams.siteIds) {
        params = params.set("siteIds", filterParams.siteIds);
      }
      if (filterParams.siteBlockIds) {
        params = params.set("siteBlockIds", filterParams.siteBlockIds);
      }
      if (filterParams.siteLevelIds) {
        params = params.set("siteLevelIds", filterParams.siteLevelIds);
      }
      if (filterParams.siteAreaIds) {
        params = params.set("siteAreaIds", filterParams.siteAreaIds);
      }
    }
    return this.http
      .get(
        `${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}/filter-meta/users`,
        {
          observe: "response",
          params: params,
        },
      )
      .pipe(
        map((resp: any) => resp),
        catchError((error) => throwError(() => error)),
      );
  }

  getAssigneeList(startDate: any, endDate: any, skills: any) {
    //v5/users/active/availability?fromDateTime=2026-08-01T00:00:00Z&toDateTime=2026-08-31T23:59:59Z
    let params = new HttpParams()
      .set("fromDateTime", startDate)
      .set("toDateTime", endDate)
      .set("skillLibraryIds", skills);

    return this.http
      .get(`${environment.base_url}${this.ENDPOINT_ASSIGNEE_USER}`, {
        observe: "response",
        params: params,
      })
      .pipe(
        map((resp: any) => resp),
        catchError((error) => throwError(() => error)),
      );
  }

  assignJobOrder(body: any) {
    //v5/job-orders/assign
    let apiUrl: string = `${environment.base_url}${this.ENDPOINT_JOB_ORDERS}/assign`;
    return this.http.put(apiUrl, body, { observe: "response" }).pipe(
      map((resp: any) => resp),
      catchError((error) => throwError(() => error)),
    );
  }

  autoAssignMonth(body: any) {
    let apiUrl: string = `${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}/generated-jobs/auto-assign`;
    return this.http.post(apiUrl, body, { observe: "response" }).pipe(
      map((resp: any) => resp),
      catchError((error) => throwError(() => error)),
    );
  }

  getJobCountsByFrequencyPerDay(body: any) {
    //site-work-scopes/generated-jobs/frequencies
    let apiUrl: string = `${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}/generated-jobs/frequencies`;
    return this.http.post(apiUrl, body, { observe: "response" }).pipe(
      map((resp: any) => resp),
      catchError((error) => throwError(() => error)),
    );
  }

  //import/export for site blocks
  getBlocksWithChildInfo(siteId?: string, contractId?: string){
    //https://dev-api.fieldex.com/custella-0.0.1-SNAPSHOT/api/site-work-scopes/import/blocks?siteId=17
    let apiUrl: string = `${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}/import/blocks`;
    let params: HttpParams = new HttpParams();
    if (siteId){
      params = params.set("siteId", siteId);
    }
    if (contractId){
      params = params.set("contractId", contractId);
    }
    return this.http.get(apiUrl, { observe: "response", params: params }).pipe(
      map((resp: any) => resp),
      catchError((error) => throwError(() => error)),
    );
  }

  getRowsCountInBulkImport(body: any){
    //api/site-work-scopes/import/template-summary
    let apiUrl: string = `${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}/import/template-summary`;
    return this.http.post(apiUrl, body, { observe: "response" }).pipe(
      map((resp: any) => resp),
      catchError((error) => throwError(() => error)),
    );
  }

  exportBulkScheduleTemplate(body: any, includeExistingFlag: boolean){
    //site-work-scopes/export/template?includeLegend=true
    let params: HttpParams = new HttpParams();
    params = params.set("includeLegend", includeExistingFlag);
    let options: any = { headers: { Accept: "application/csv" } };
    options.responseType = "blob";
    options.observe = "response";
    options.params = params;

    let apiUrl: string = `${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}/export/template`;
    
    return this.http.post<Blob>(apiUrl, body, options).pipe(
      map((resp: any) => resp),
      catchError((error) => throwError(() => error)),
    );
  }
  
  uploadCSVTemplate(formData: any){
    //site-work-scopes/import/preview?siteId&contractId&createMissing
    return this.http
      .post(
        `${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}/import/preview`,
        formData,
      )
      .pipe(
        map((resp: any) => resp),
        catchError((error) => throwError(() => error)),
      );
  }

  importBulkWorkScopes(body: any){
    //site-work-scopes/import
    let apiUrl: string = `${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}/import`;
    return this.http.post(apiUrl, body, { observe: "response" }).pipe(
      map((resp: any) => resp),
      catchError((error) => throwError(() => error)),
    );
  }

  deleteJOForMonth(body: any){
    //{{ip}}/api/site-work-scopes/job-orders
    let apiUrl: string = `${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}/job-orders`;
    return this.http.request('delete', apiUrl, { body: body, observe: "response" }).pipe(
      map((resp: any) => resp),
      catchError((error) => throwError(() => error)),
    );

  }

  appendAssetsToBlock(body: any){
    //api/assets/location
    let apiUrl: string = `${environment.base_url}${this.ENDPOINT_APPEND_ASSETS_TO_BLOCK}`;
    return this.http.put(apiUrl, body, { observe: "response" }).pipe(
      map((resp: any) => resp),
      catchError((error) => throwError(() => error)),
    );
  }

   getJOCountsToBeDeleted(body: any){
    let apiUrl: string = `${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}/job-orders/count`;
    return this.http.put(apiUrl, body, { observe: "response" }).pipe(
      map((resp: any) => resp),
      catchError((error) => throwError(() => error)),
    );
  }

  bulkReassignJobs(body: any, params: any){
    //{{ip}}/api/site-work-scopes/generated-jobs/bulk-reassign?previousJobOwnerId&newJobOwnerId=1
    let apiUrl: string = `${environment.base_url}${this.ENDPOINT_SITE_WORK_SCOPE}/generated-jobs/bulk-reassign?previousJobOwnerId=${params.previousJobOwnerId}&newJobOwnerId=${params.newJobOwnerId}`;
    return this.http.post(apiUrl, body, { observe: "response" }).pipe(
      map((resp: any) => resp),
      catchError((error) => throwError(() => error)),
    );
  }
  deleteAssetFromSite(assetId: any){
    let apiUrl: string = `${environment.base_url}${this.ENDPOINT_DELETE_ASSET_FROM_SITE}?assetsId=${assetId}`;
    return this.http.put(apiUrl, {}, { observe: "response" }).pipe(
      map((resp: any) => resp),
      catchError((error) => throwError(() => error)),
    );
  }
}
// Summary of changes:

// ✅ Removed the commented import:

// The import was already commented out: //import { Headers, Http, RequestOptions } from "@angular/http";


// ✅ Updated constructor:

// Removed private httpOld: Http parameter


// ✅ Updated uploadAttachment method:

// Changed from using Headers and RequestOptions to HttpHeaders
// Changed from httpOld (old Http service) to http (HttpClient)
// Simplified the headers configuration



// The code should now compile successfully with Angular 19!